import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  BehaviorSubject,
  debounceTime,
  Observable,
  skipUntil,
  Subject,
  tap,
  timer,
} from 'rxjs';
import { Answer } from '../models/answers/answer.model';
import { QuestionStructureOptions } from '../models/question/question-options.model';
import { QuestionType } from '../models/question/question-type.model';
import { Question } from '../models/question/question.model';

@Injectable({
  providedIn: 'root',
})
export class QuestionValueChangeService {
  private initialAnswers: Answer[];

  private valueChanges = new Subject<Answer>();
  valueChanges$ = this.valueChanges.asObservable();

  private removeRepeaterGroupSubject = new Subject<string>();
  removeRepeaterGroup$ = this.removeRepeaterGroupSubject.asObservable();

  private keyValueChanges: Subject<{
    answer: Answer;
    valid: boolean;
  }>[] = [];
  keyValueChanges$: Observable<{
    answer: Answer;
    valid: boolean;
  }>[] = [];
  private ansokanCreatedDate: Date;

  private patchValueSubjects = new Map<string, Subject<Answer>>();

  public valueChangeListener(
    formControl: AbstractControl,
    answer: Answer,
    question: Question,
  ) {
    return formControl.valueChanges.pipe(
      skipUntil(timer(100)), // timer prevents initial patchvalue to trigger valueChange
      debounceTime(300),
      tap((value) => {
        if (formControl.dirty) {
          const answerValue = this.getAnswerValue(
            answer,
            value,
            question.type,
            question.key,
            formControl.valid,
            question,
          );
          this.valueChanges.next(answerValue);
          this.createOrSendKeyValue(answerValue);
        }
      }),
    );
  }

  public setInitValues(answers: Answer[]) {
    this.initialAnswers = answers;
  }

  public setCreatedDate(created: Date) {
    this.ansokanCreatedDate = created;
  }
  public getCreatedDate() {
    return this.ansokanCreatedDate;
  }

  public patchInitialValue(
    questionId: string,
    repeaterGroupId: string | undefined,
    control: AbstractControl,
    question: Question,
  ) {
    const initialAnswer = this.initialAnswers?.find(
      (a) =>
        a?.questionId === questionId &&
        (!repeaterGroupId || a?.repeaterGroupId === repeaterGroupId),
    );
    if (initialAnswer && initialAnswer.value && initialAnswer.value !== '{}') {
      this.patchValue(control, initialAnswer, question);
    }
  }

  public sendInitialKeyValue(
    question: Question,
    control: AbstractControl,
    answer: Answer,
    valid: boolean,
  ) {
    const answerValue = this.getAnswerValue(
      answer,
      control.value,
      question.type,
      question.key,
      valid,
    );
    this.createOrSendKeyValue(answerValue);
  }

  public removeRepeaterGroup(repeaterGroupId: string) {
    this.removeRepeaterGroupSubject.next(repeaterGroupId);
  }

  public patchQuestionValue(answer: Answer) {
    const key = answer.repeaterGroupId
      ? `${answer.repeaterGroupId}_${answer.questionId}`
      : answer.questionId;
    let subject = this.patchValueSubjects.get(key);
    if (!subject) {
      subject = new Subject<Answer>();
      this.patchValueSubjects.set(key, subject);
    }
    subject.next(answer);

    this.createOrSendKeyValue(answer);
  }

  public patchValueListener(
    question: Question,
    repeaterGroupId: string | undefined,
    formControl: AbstractControl,
  ) {
    const key = repeaterGroupId
      ? `${repeaterGroupId}_${question.id}`
      : question.id;
    let subject = this.patchValueSubjects.get(key);
    if (!subject) {
      subject = new Subject<Answer>();
      this.patchValueSubjects.set(key, subject);
    }

    return subject.pipe(
      tap((answer) => this.patchValue(formControl, answer, question, false)),
    );
  }

  private getAnswerValue(
    answer: Answer,
    value,
    questionType: QuestionType,
    key: string,
    valid: boolean,
    question?: Question,
  ): Answer {
    if (value == undefined) {
      return { ...answer, value, key, questionType, valid };
    }

    switch (questionType) {
      case QuestionType.Dropdown:
      case QuestionType.RadioButton: {
        return {
          ...answer,
          value: (question.structure as QuestionStructureOptions)
            .dropdownMultiple
            ? value
            : JSON.stringify([value]),
          key,
          questionType,
          valid,
        };
      }
      case QuestionType.FileUpload:
      case QuestionType.Signering: {
        return {
          ...answer,
          value: JSON.stringify(value),
          key,
          questionType,
          valid,
        };
      }
      case QuestionType.Input: {
        return { ...answer, value: value.toString(), key, questionType, valid };
      }
      default: {
        return { ...answer, value: value, key, questionType, valid };
      }
    }
  }

  private patchValue(
    control: AbstractControl,
    answer: Answer,
    question: Question,
    emitEvent = true,
  ) {
    switch (question.type) {
      case QuestionType.Dropdown:
      case QuestionType.RadioButton: {
        control.patchValue(
          (question.structure as QuestionStructureOptions).dropdownMultiple
            ? answer.value
            : (JSON.parse(answer.value) ?? [])[0],
          { emitEvent },
        );
        break;
      }
      case QuestionType.FileUpload:
      case QuestionType.Signering: {
        const uploadedFiles = JSON.parse(answer.value);
        control.patchValue(uploadedFiles, { emitEvent });
        break;
      }
      default: {
        control.patchValue(answer.value, { emitEvent });
        break;
      }
    }
    if (!answer.isEffektaValues) {
      control.markAsTouched();
      control.markAsDirty();
    }
  }

  public createOrSendKeyValue(answer: Answer) {
    if (answer.key) {
      if (this.keyValueChanges[answer.key]) {
        this.keyValueChanges[answer.key].next({ answer, valid: answer.valid });
      } else {
        this.keyValueChanges[answer.key] = new BehaviorSubject<{
          answer: Answer;
          valid: boolean;
        }>({ answer, valid: answer.valid });
        this.keyValueChanges$[answer.key] =
          this.keyValueChanges[answer.key].asObservable();
      }
    }
  }

  public getOrCreateKeyValue(
    key: string,
  ): Observable<{ answer: Answer; valid: boolean }> {
    const obs = this.keyValueChanges$[key];
    if (obs) {
      return obs;
    }
    this.keyValueChanges[key] = new BehaviorSubject<{
      answer: Answer;
      valid: boolean;
    }>(undefined);
    this.keyValueChanges$[key] = this.keyValueChanges[key].asObservable();
  }
}
